"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/Button";
import { Upload, X, MoreHorizontal, ImageIcon, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/DropdownMenu";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableImage({ image, onRemove, onSetCover, onChangePicture }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative group touch-manipulation"
    >
      <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-gray-200 shadow-sm">
        <img
          src={image.url || "/placeholder.svg"}
          alt="Uploaded image"
          className="w-full h-full object-cover"
        />

        {/* Cover Image Label */}
        {image.isCover && (
          <div className="absolute top-3 left-3 bg-white text-black text-xs px-3 py-1 rounded-md font-medium shadow-sm">
            Cover Image
          </div>
        )}

        {/* Quick Remove Button */}
        <button
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 text-gray-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-white"
          onClick={(e) => {
            e.stopPropagation(); // Prevent drag interaction when removing
            onRemove(image.id);
          }}
        >
          <X className="h-4 w-4" />
        </button>

        {/* Options Menu */}
      </div>
    </div>
  );
}

export function ImageUpload({
  category = "House",
  minPhotos = 6,
  images: externalImages,
  onChange,
}) {
  const [images, setImages] = useState(externalImages || []);
  const fileInputRef = useRef(null);
  const changeImageRef = useRef(null);

  // Sync with external state if provided
  useEffect(() => {
    // Only update if externalImages are truly different to prevent infinite loops
    // and if there's an onChange handler
    if (onChange && JSON.stringify(images) !== JSON.stringify(externalImages)) {
      onChange(images);
    }
  }, [images, externalImages, onChange]);

  // Initialize from external images if provided, but only once on mount or when externalImages changes significantly
  useEffect(() => {
    if (externalImages && externalImages.length > 0 && images.length === 0) {
      setImages(externalImages);
    }
  }, [externalImages]); // Depend only on externalImages to avoid re-initializing images state unnecessarily

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const newImagesToAdd = newFiles.map((file) => ({
        id: crypto.randomUUID(),
        url: URL.createObjectURL(file),
        file: file, // Store actual file for form submission
        isCover: false, // Default to false, will be set appropriately below
      }));

      if (changeImageRef.current) {
        // Replace specific image
        setImages((prev) => {
          const updatedImages = prev.map((img) =>
            img.id === changeImageRef.current
              ? { ...newImagesToAdd[0], isCover: img.isCover } // Keep existing cover status
              : img
          );
          return updatedImages;
        });
        changeImageRef.current = null;
      } else {
        // Add new images
        setImages((prev) => {
          const updatedImages = [...prev, ...newImagesToAdd];
          // Ensure the first image is always the cover
          // If there's no cover, or the cover was just added and it's the first image, set it.
          if (
            updatedImages.length > 0 &&
            !updatedImages.some((img) => img.isCover)
          ) {
            updatedImages[0] = { ...updatedImages[0], isCover: true };
          }
          return updatedImages;
        });
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (id) => {
    setImages((prev) => {
      const isCoverRemoved = prev.find((img) => img.id === id)?.isCover;
      const newImages = prev.filter((img) => img.id !== id);

      // If the removed image was the cover and there are images remaining,
      // set the first remaining image as the new cover.
      if (isCoverRemoved && newImages.length > 0) {
        newImages[0] = { ...newImages[0], isCover: true };
      }
      return newImages;
    });
  };

  const setCoverImage = (id) => {
    setImages((prev) =>
      prev.map((img) => ({
        ...img,
        isCover: img.id === id,
      }))
    );
  };

  const changePicture = (id) => {
    changeImageRef.current = id;
    fileInputRef.current?.click();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const updatedImages = arrayMove(items, oldIndex, newIndex);
        // After reordering, ensure the first image is always the cover
        updatedImages.forEach((img, index) => {
          img.isCover = index === 0;
        });
        return updatedImages;
      });
    }
  };

  return (
    <div className="space-y-4">
      {images.length === 0 ? (
        <div className="space-y-4">
          <div className="text-lg font-medium font-fraunces">
            Add Some photos of your {category}
          </div>
          <div className="text-sm text-gray-500">
            You'll need to add at least {minPhotos} photos
          </div>
          <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors p-6">
            <div className="flex flex-col items-center justify-center">
              <div className="w-16 h-16 mb-2 flex items-center justify-center border-2 rounded-md">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-base font-medium mb-1">+ Add Photos</p>
              <p className="text-sm text-gray-500">Browse from your computer</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
            />
          </label>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center">
            <div>
              <div className="text-lg font-medium">Looks Great</div>
              <div className="text-sm text-gray-500">Drag to reorder</div>
            </div>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 rounded-full px-4 py-2 border border-gray-300 hover:bg-gray-50"
            >
              <span className="text-sm font-medium">+ Add more Image</span>
            </Button>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map((img) => img.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                {images.map((image) => (
                  <SortableImage
                    key={image.id}
                    image={image}
                    onRemove={removeImage}
                    onSetCover={setCoverImage}
                    onChangePicture={changePicture}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
          />
        </>
      )}
    </div>
  );
}
